import { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuthStore from "../store/useAuthStore";
import { deleteCard, getCards } from "../services/CardService";
import {
  getPabblyEmailTemplates,
  getTemplate,
} from "../services/TemplateServices";
import { sendEmail } from "../services/UserServices";

const PAGE_SIZE = 12;

const Icon = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    phone: <path d="M7.2 3h3l1.5 4-2 1.5a15 15 0 0 0 5.8 5.8l1.5-2 4 1.5v3c0 2-1.5 3.2-3.3 3.2C10.1 20 4 13.9 4 6.3 4 4.5 5.2 3 7.2 3Z" />,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
    building: <><path d="M4 21V5l8-3v19M12 8h8v13M2 21h20" /><path d="M7 7h2M7 11h2M7 15h2M15 12h2M15 16h2" /></>,
    briefcase: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2" /></>,
    download: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M5 21h14" /></>,
    trash: <><path d="M4 7h16M9 3h6l1 4H8l1-4ZM7 7l1 14h8l1-14M10 11v6M14 11v6" /></>,
    arrow: <><path d="M5 12h14M14 7l5 5-5 5" /></>,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    logout: <><path d="M10 5H5v14h5M14 8l4 4-4 4M8 12h10" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 15-4-4L5 20" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 8a7 7 0 0 1 11.5-2L20 8M4 16l2.4 2a7 7 0 0 0 11.5-2" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0M16 5a3 3 0 0 1 0 6M17 14a5 5 0 0 1 4 5" /></>,
  };

  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

const getFullName = (card) =>
  `${card?.firstName || ""} ${card?.lastName || ""}`.trim() || "Unnamed contact";

const getInitials = (card) => {
  const initials = [card?.firstName, card?.lastName]
    .filter(Boolean)
    .map((part) => part.trim()[0])
    .join("")
    .slice(0, 2);
  return initials.toUpperCase() || "?";
};

const getWebsiteUrl = (website = "") => {
  if (!website) return "";
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
};

const formatDate = (date) => {
  if (!date) return "Date unavailable";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
};

const ContactLink = ({ icon, href, children }) => {
  const content = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500">
        <Icon name={icon} className="h-4 w-4" />
      </span>
      <span className="min-w-0 truncate">{children || "Not available"}</span>
    </>
  );

  return href ? (
    <a
      href={href}
      target={icon === "globe" ? "_blank" : undefined}
      rel={icon === "globe" ? "noreferrer" : undefined}
      className="flex min-w-0 items-center gap-2.5 rounded-xl text-sm font-medium text-slate-600 transition hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      {content}
    </a>
  ) : (
    <div className="flex min-w-0 items-center gap-2.5 text-sm font-medium text-slate-400">
      {content}
    </div>
  );
};

const CardSkeleton = () => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
    <div className="aspect-[1.65/1] animate-pulse bg-slate-200" />
    <div className="space-y-3 p-4">
      <div className="h-5 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
      <div className="mt-5 h-10 animate-pulse rounded-xl bg-slate-100" />
      <div className="h-10 animate-pulse rounded-xl bg-slate-100" />
    </div>
  </div>
);

const ImageFallback = ({ card, compact = false }) => (
  <div className="grid h-full w-full place-items-center bg-gradient-to-br from-indigo-100 via-white to-violet-100">
    <span
      className={`grid place-items-center rounded-2xl bg-white font-bold text-indigo-700 shadow-sm ring-1 ring-indigo-100 ${
        compact ? "h-14 w-14 text-lg" : "h-20 w-20 text-2xl"
      }`}
    >
      {getInitials(card)}
    </span>
  </div>
);

const TemplateOption = ({ template, provider, selected, onSelect }) => {
  const value = `${provider}:${template.id}`;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(value)}
      className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        selected
          ? "border-indigo-500 bg-indigo-50 shadow-sm"
          : "border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50"
      }`}
    >
      <span
        className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border ${
          selected
            ? "border-indigo-600 bg-indigo-600 text-white"
            : "border-slate-300 bg-white text-transparent"
        }`}
      >
        <Icon name="check" className="h-3 w-3" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold text-slate-900">
            {template.name}
          </span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              provider === "pabbly"
                ? "bg-violet-100 text-violet-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {provider === "pabbly" ? "Pabbly" : "Saved"}
          </span>
        </span>
        {template.subject && (
          <span className="mt-1 block truncate text-xs text-slate-500">
            {template.subject}
          </span>
        )}
      </span>
    </button>
  );
};

const CardsPage = () => {
  const { role, token } = useAuthStore();
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const [cards, setCards] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedCard, setSelectedCard] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [templateModal, setTemplateModal] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [pabblyTemplateList, setPabblyTemplateList] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [pabblyTemplateError, setPabblyTemplateError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => window.clearTimeout(handler);
  }, [searchTerm]);

  const fetchCards = useCallback(async (currentOffset, currentSearch, isNewSearch = false) => {
    if (!token) return;
    if (!isNewSearch && loadingMoreRef.current) return;

    const requestId = isNewSearch ? ++requestIdRef.current : requestIdRef.current;
    if (isNewSearch) {
      setInitialLoading(true);
      setFetchError("");
      setTotalCount(0);
    } else {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const response = await getCards(PAGE_SIZE, currentOffset, currentSearch);
      if (response.status !== 200) throw new Error("Cards could not be loaded");
      if (requestId !== requestIdRef.current) return;

      const newCards = Array.isArray(response.data?.cards) ? response.data.cards : [];
      const count = Number(response.data?.count ?? currentOffset + newCards.length);

      setCards((currentCards) => {
        if (isNewSearch) return newCards;
        const byId = new Map(currentCards.map((card) => [String(card.id), card]));
        newCards.forEach((card) => byId.set(String(card.id), card));
        return [...byId.values()];
      });
      setOffset(currentOffset + newCards.length);
      setTotalCount(count);
      setHasMore(currentOffset + newCards.length < count);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error fetching cards:", error);
      setFetchError(error.response?.data?.message || "Cards could not be loaded");
      if (!isNewSearch) setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        setInitialLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setCards([]);
    setOffset(0);
    setHasMore(true);
    fetchCards(0, debouncedSearch, true);
  }, [debouncedSearch, fetchCards, token]);

  const loadMore = useCallback(() => {
    if (hasMore && !initialLoading && !loadingMore) {
      fetchCards(offset, debouncedSearch, false);
    }
  }, [debouncedSearch, fetchCards, hasMore, initialLoading, loadingMore, offset]);

  useEffect(() => {
    if (!selectedCard && !templateModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (templateModal) {
        setTemplateModal(false);
        setSelectedTemplateId("");
      } else {
        setSelectedCard(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedCard, templateModal]);

  const openCard = (card) => {
    setSelectedCard(card);
    setActiveImage(card.imageUrl || card.backImageUrl || card.userImage || "");
  };

  const closeCard = () => {
    setSelectedCard(null);
    setActiveImage("");
  };

  const allTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setPabblyTemplateError("");
    const [localResult, pabblyResult] = await Promise.allSettled([
      getTemplate(),
      getPabblyEmailTemplates(),
    ]);

    if (localResult.status === "fulfilled") {
      setTemplateList(localResult.value?.data?.data || []);
    } else {
      setTemplateList([]);
      console.error("Error fetching saved templates:", localResult.reason);
    }

    if (pabblyResult.status === "fulfilled") {
      setPabblyTemplateList(
        pabblyResult.value?.data?.templates?.filter(
          (template) => template?.id && template?.name
        ) || []
      );
    } else {
      setPabblyTemplateList([]);
      setPabblyTemplateError(
        pabblyResult.reason?.response?.data?.message ||
          "Pabbly templates could not be loaded"
      );
    }
    setTemplatesLoading(false);
  }, []);

  const openEmailModal = (email) => {
    if (!email) {
      toast.error("This card does not have an email address");
      return;
    }
    setSelectedUser(email);
    setSelectedTemplateId("");
    setTemplateSearch("");
    setTemplateModal(true);
    allTemplates();
  };

  const closeEmailModal = () => {
    if (sendingEmail) return;
    setTemplateModal(false);
    setSelectedTemplateId("");
    setTemplateSearch("");
  };

  const handleSendEmail = async () => {
    const separatorIndex = selectedTemplateId.indexOf(":");
    const provider = selectedTemplateId.slice(0, separatorIndex);
    const templateId = selectedTemplateId.slice(separatorIndex + 1);
    if (!provider || !templateId) {
      toast.error("Choose an email template first");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await sendEmail(templateId, selectedUser, provider);
      toast.success(response?.data?.message || "Email sent");
      setTemplateModal(false);
      setSelectedTemplateId("");
      setTemplateSearch("");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Email could not be sent");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm(`Delete ${getFullName(card)}? This cannot be undone.`)) return;
    try {
      const response = await deleteCard(card.id);
      if (response.status === 200) {
        toast.success(response?.data?.message || "Card deleted");
        setCards((currentCards) =>
          currentCards.filter((item) => String(item.id) !== String(card.id))
        );
        setTotalCount((count) => Math.max(0, count - 1));
        if (selectedCard?.id === card.id) closeCard();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Card could not be deleted");
    }
  };

  const escapeVCardValue = (value = "") =>
    String(value)
      .replace(/\\/g, "\\\\")
      .replace(/\r?\n/g, "\\n")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,");

  const downloadCard = (card) => {
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `N:${escapeVCardValue(card.lastName)};${escapeVCardValue(card.firstName)};;;`,
      `FN:${escapeVCardValue(getFullName(card))}`,
      card.company ? `ORG:${escapeVCardValue(card.company)}` : null,
      card.position ? `TITLE:${escapeVCardValue(card.position)}` : null,
      card.mobile ? `TEL;TYPE=CELL:${escapeVCardValue(card.mobile)}` : null,
      card.email ? `EMAIL:${escapeVCardValue(card.email)}` : null,
      card.website ? `URL:${escapeVCardValue(getWebsiteUrl(card.website))}` : null,
      "END:VCARD",
    ].filter(Boolean);
    const blob = new Blob([lines.join("\r\n")], {
      type: "text/vcard;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const safeName = getFullName(card).replace(/[^a-z0-9_-]+/gi, "_");
    anchor.href = url;
    anchor.download = `${safeName || "contact"}.vcf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const normalizedTemplateSearch = templateSearch.trim().toLowerCase();
  const filterTemplates = (templates) =>
    templates.filter((template) =>
      `${template.name || ""} ${template.subject || ""}`
        .toLowerCase()
        .includes(normalizedTemplateSearch)
    );
  const filteredSavedTemplates = filterTemplates(templateList);
  const filteredPabblyTemplates = filterTemplates(pabblyTemplateList);
  const cardImages = selectedCard
    ? [
        { label: "Front", url: selectedCard.imageUrl },
        { label: "Back", url: selectedCard.backImageUrl },
        { label: "Visitor", url: selectedCard.userImage },
      ].filter((image) => image.url)
    : [];
  const activeImageLabel =
    cardImages.find((image) => image.url === activeImage)?.label ||
    cardImages[0]?.label ||
    "Card";

  return (
    <main className="w-full px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/70 to-violet-100/70 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/70 sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-indigo-300/20 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                <Icon name="users" className="h-4 w-4" /> Contact workspace
              </span>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                Card library
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
                Find every scanned contact, review their card, or continue the conversation.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex min-h-10 items-center rounded-xl bg-white px-3.5 text-sm font-bold text-slate-700 shadow-sm ring-1 ring-slate-200">
                {initialLoading ? "…" : totalCount} {totalCount === 1 ? "card" : "cards"}
              </span>
            </div>
          </div>
        </header>

        <section className="sticky top-16 z-30 -mx-4 mt-4 border-y border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:static sm:mx-0 sm:mt-6 sm:rounded-2xl sm:border sm:bg-white sm:p-4 sm:shadow-sm sm:dark:bg-slate-900">
          <div className="relative">
            <Icon name="search" className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search name, company, email, or mobile"
              aria-label="Search cards"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="min-h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-11 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
            />
            {searchTerm && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <Icon name="close" className="h-4 w-4" />
              </button>
            )}
          </div>
          {debouncedSearch && !initialLoading && (
            <p className="mt-2 px-1 text-xs font-medium text-slate-500">
              {totalCount} {totalCount === 1 ? "result" : "results"} for “{debouncedSearch}”
            </p>
          )}
        </section>

        <section className="mt-5 sm:mt-6" aria-live="polite">
          {initialLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 8 }, (_, index) => <CardSkeleton key={index} />)}
            </div>
          ) : fetchError && cards.length === 0 ? (
            <div className="rounded-3xl border border-rose-200 bg-white px-5 py-12 text-center shadow-sm">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-rose-50 text-rose-600">
                <Icon name="refresh" className="h-6 w-6" />
              </span>
              <h2 className="mt-4 text-lg font-bold text-slate-900">Cards could not be loaded</h2>
              <p className="mt-1 text-sm text-slate-500">{fetchError}</p>
              <button
                type="button"
                onClick={() => fetchCards(0, debouncedSearch, true)}
                className="mt-5 min-h-11 rounded-xl bg-slate-900 px-5 text-sm font-bold text-white hover:bg-slate-800"
              >
                Try again
              </button>
            </div>
          ) : cards.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white px-5 py-14 text-center shadow-sm">
              <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-indigo-50 text-indigo-600">
                <Icon name="search" className="h-7 w-7" />
              </span>
              <h2 className="mt-4 text-lg font-bold text-slate-900">
                {debouncedSearch ? "No matching cards" : "No cards saved yet"}
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                {debouncedSearch
                  ? "Try a different name, company, email, or mobile number."
                  : "Scan your first business card and it will appear here."}
              </p>
              {debouncedSearch && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="mt-5 min-h-11 rounded-xl bg-indigo-600 px-5 text-sm font-bold text-white hover:bg-indigo-700"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <InfiniteScroll
              dataLength={cards.length}
              next={loadMore}
              hasMore={hasMore}
              loader={null}
              endMessage={
                <p className="py-7 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  All cards loaded
                </p>
              }
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {cards.map((card) => (
                  <article
                    key={card.id}
                    className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-xl hover:shadow-slate-200/70"
                  >
                    <button
                      type="button"
                      onClick={() => openCard(card)}
                      className="relative block aspect-[1.65/1] w-full overflow-hidden bg-slate-100 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-indigo-500"
                      aria-label={`View ${getFullName(card)}`}
                    >
                      {card.imageUrl ? (
                        <>
                          <img
                            src={card.imageUrl}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="absolute inset-0 hidden h-full w-full scale-110 object-cover opacity-40 blur-xl lg:block"
                          />
                          <span className="absolute inset-0 hidden bg-gradient-to-br from-slate-900/20 via-slate-800/5 to-indigo-950/25 lg:block" />
                          <img
                            src={card.imageUrl}
                            alt=""
                            loading="lazy"
                            className="relative h-full w-full object-cover transition duration-300 group-hover:scale-[1.03] lg:object-contain lg:p-3 lg:drop-shadow-xl lg:group-hover:scale-[1.01]"
                          />
                        </>
                      ) : (
                        <ImageFallback card={card} compact />
                      )}
                      <span className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950/65 to-transparent" />
                      <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-700 shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-slate-200">
                        <Icon name="image" className="h-3.5 w-3.5" /> View card
                      </span>
                    </button>

                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="truncate text-base font-bold text-slate-950">
                            {getFullName(card)}
                          </h2>
                          <p className="mt-1 truncate text-xs font-medium text-slate-500">
                            {[card.position, card.company].filter(Boolean).join(" · ") || "Contact details"}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                          {formatDate(card.createdAt)}
                        </span>
                      </div>

                      <div className="mt-4 space-y-3">
                        <ContactLink icon="mail" href={card.email ? `mailto:${card.email}` : ""}>
                          {card.email}
                        </ContactLink>
                        <ContactLink icon="phone" href={card.mobile ? `tel:${card.mobile}` : ""}>
                          {card.mobile}
                        </ContactLink>
                      </div>

                      <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
                        <button
                          type="button"
                          onClick={() => openCard(card)}
                          className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 text-xs font-bold text-white transition hover:bg-slate-800"
                        >
                          Details <Icon name="arrow" className="h-4 w-4" />
                        </button>
                        {role === "admin" && (
                          <>
                            <button
                              type="button"
                              aria-label={`Download ${getFullName(card)} contact`}
                              title="Download contact"
                              onClick={() => downloadCard(card)}
                              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                            >
                              <Icon name="download" className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Delete ${getFullName(card)}`}
                              title="Delete card"
                              onClick={() => handleDelete(card)}
                              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-400 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                            >
                              <Icon name="trash" className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              {loadingMore && (
                <div className="flex items-center justify-center gap-2 py-7 text-sm font-semibold text-slate-500">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                  Loading more cards…
                </div>
              )}
            </InfiniteScroll>
          )}
        </section>
      </div>

      {selectedCard && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-5"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) closeCard();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="card-detail-title"
            className="flex max-h-[92dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-3xl"
          >
            <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-6 sm:py-4">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600">Contact details</p>
                <h2 id="card-detail-title" className="mt-0.5 truncate text-lg font-bold text-slate-950">
                  {getFullName(selectedCard)}
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close card details"
                onClick={closeCard}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-900"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </header>

            <div className="overflow-y-auto">
              <div className="grid lg:grid-cols-[minmax(0,1.18fr)_minmax(22rem,0.82fr)]">
                <div className="bg-gradient-to-br from-slate-950 via-slate-950 to-indigo-950 p-3 sm:p-5 lg:flex lg:min-h-[30rem] lg:flex-row-reverse lg:gap-4">
                  <div className="relative aspect-[1.58/1] overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-2xl lg:min-h-[27.5rem] lg:flex-1 lg:aspect-auto">
                    {activeImage ? (
                      <>
                        <img
                          src={activeImage}
                          alt=""
                          aria-hidden="true"
                          className="absolute inset-0 hidden h-full w-full scale-110 object-cover opacity-25 blur-2xl lg:block"
                        />
                        <span className="absolute inset-0 hidden bg-gradient-to-br from-slate-950/25 via-transparent to-indigo-950/30 lg:block" />
                        <img
                          src={activeImage}
                          alt={`${activeImageLabel} card view`}
                          className="relative h-full w-full object-contain lg:absolute lg:inset-0 lg:p-5 lg:drop-shadow-2xl"
                        />
                        <div className="absolute inset-x-4 top-4 hidden items-center justify-between gap-3 lg:flex">
                          <span className="rounded-full border border-white/10 bg-slate-950/75 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white shadow-lg backdrop-blur">
                            {activeImageLabel} image
                          </span>
                          <span className="rounded-full border border-white/10 bg-slate-950/75 px-2.5 py-1.5 text-[10px] font-bold text-slate-300 shadow-lg backdrop-blur">
                            {cardImages.length} {cardImages.length === 1 ? "image" : "images"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <ImageFallback card={selectedCard} />
                    )}
                  </div>
                  {cardImages.length > 1 && (
                    <div className="mt-3 grid grid-cols-3 gap-2 lg:mt-0 lg:w-24 lg:shrink-0 lg:grid-cols-1 lg:content-start">
                      {cardImages.map((image) => (
                        <button
                          key={image.label}
                          type="button"
                          onClick={() => setActiveImage(image.url)}
                          aria-label={`Show ${image.label.toLowerCase()} image`}
                          aria-pressed={activeImage === image.url}
                          className={`relative overflow-hidden rounded-xl border-2 text-left transition ${
                            activeImage === image.url
                              ? "border-indigo-400 shadow-lg shadow-indigo-950/40"
                              : "border-transparent opacity-75 hover:border-slate-600 hover:opacity-100"
                          }`}
                        >
                          <span className="block aspect-[1.58/1] bg-slate-800 lg:aspect-[1.15/1]">
                            <img src={image.url} alt="" className="h-full w-full object-cover" />
                          </span>
                          <span className="block bg-slate-900 px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-wide text-slate-300">
                            {image.label}
                          </span>
                          {activeImage === image.url && (
                            <span className="absolute right-1.5 top-1.5 hidden h-5 w-5 place-items-center rounded-full bg-indigo-500 text-white shadow lg:grid">
                              <Icon name="check" className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-4 sm:p-6">
                  <div className="flex items-start gap-3">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-indigo-100 text-base font-bold text-indigo-700">
                      {getInitials(selectedCard)}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">
                        {getFullName(selectedCard)}
                      </h3>
                      <p className="mt-1 text-sm font-medium text-slate-500">
                        {[selectedCard.position, selectedCard.company].filter(Boolean).join(" · ") || "Contact"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 rounded-2xl border border-slate-200 p-4">
                    <ContactLink icon="mail" href={selectedCard.email ? `mailto:${selectedCard.email}` : ""}>{selectedCard.email}</ContactLink>
                    <ContactLink icon="phone" href={selectedCard.mobile ? `tel:${selectedCard.mobile}` : ""}>{selectedCard.mobile}</ContactLink>
                    <ContactLink icon="globe" href={getWebsiteUrl(selectedCard.website)}>{selectedCard.website}</ContactLink>
                  </div>

                  <dl className="mt-5 grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <dt className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-slate-400"><Icon name="building" className="h-3.5 w-3.5" /> Company</dt>
                      <dd className="mt-1.5 text-sm font-semibold text-slate-800">{selectedCard.company || "Not available"}</dd>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-3">
                      <dt className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-slate-400"><Icon name="briefcase" className="h-3.5 w-3.5" /> Position</dt>
                      <dd className="mt-1.5 text-sm font-semibold text-slate-800">{selectedCard.position || "Not available"}</dd>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-3 min-[420px]:col-span-2">
                      <dt className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-slate-400"><Icon name="calendar" className="h-3.5 w-3.5" /> Saved</dt>
                      <dd className="mt-1.5 text-sm font-semibold text-slate-800">{formatDate(selectedCard.createdAt)}</dd>
                    </div>
                  </dl>

                  {selectedCard.remark && (
                    <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Remark</p>
                      <p className="mt-1.5 whitespace-pre-wrap text-sm leading-6 text-slate-700">{selectedCard.remark}</p>
                    </div>
                  )}

                  {selectedCard.campaignProfileName && (
                    <div className="mt-4 inline-flex rounded-full bg-violet-100 px-3 py-1.5 text-xs font-bold text-violet-700">
                      Profile: {selectedCard.campaignProfileName}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="mobile-safe-bottom border-t border-slate-200 bg-white p-4 sm:flex sm:items-center sm:justify-between sm:gap-3 sm:px-6">
              {role === "admin" ? (
                <>
                  <button
                    type="button"
                    onClick={() => downloadCard(selectedCard)}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 sm:w-auto"
                  >
                    <Icon name="download" className="h-4 w-4" /> Save contact
                  </button>
                <div className="mt-2 grid grid-cols-[auto_1fr] gap-2 sm:mt-0 sm:flex">
                  <button
                    type="button"
                    aria-label="Delete card"
                    onClick={() => handleDelete(selectedCard)}
                    className="grid h-11 w-11 place-items-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                  >
                    <Icon name="trash" className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEmailModal(selectedCard.email)}
                    disabled={!selectedCard.email}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition hover:from-indigo-700 hover:to-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon name="mail" className="h-4 w-4" /> Send email
                  </button>
                </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={closeCard}
                  className="min-h-11 w-full rounded-xl bg-slate-950 px-5 text-sm font-bold text-white sm:ml-auto sm:w-auto"
                >
                  Close
                </button>
              )}
            </footer>
          </section>
        </div>
      )}

      {templateModal && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-5"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) closeEmailModal();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-template-title"
            className="flex max-h-[90dvh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-6 sm:py-5">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600">Email contact</p>
                <h2 id="email-template-title" className="mt-1 text-xl font-bold text-slate-950">Choose a template</h2>
                <p className="mt-1 text-sm text-slate-500">Sending to <span className="font-semibold text-slate-700">{selectedUser}</span></p>
              </div>
              <button
                type="button"
                aria-label="Close email templates"
                onClick={closeEmailModal}
                disabled={sendingEmail}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="relative">
                <Icon name="search" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={templateSearch}
                  onChange={(event) => setTemplateSearch(event.target.value)}
                  placeholder="Search templates"
                  aria-label="Search email templates"
                  className="min-h-11 w-full rounded-xl border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                />
              </div>

              {templatesLoading ? (
                <div className="mt-4 space-y-2">
                  {Array.from({ length: 4 }, (_, index) => (
                    <div key={index} className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  ))}
                </div>
              ) : (
                <div className="mt-5 space-y-5" role="radiogroup" aria-label="Email templates">
                  {filteredSavedTemplates.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Saved templates</h3>
                      <div className="space-y-2">
                        {filteredSavedTemplates.map((template) => (
                          <TemplateOption key={`local-${template.id}`} template={template} provider="local" selected={selectedTemplateId === `local:${template.id}`} onSelect={setSelectedTemplateId} />
                        ))}
                      </div>
                    </div>
                  )}
                  {filteredPabblyTemplates.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-600">Pabbly templates</h3>
                      <div className="space-y-2">
                        {filteredPabblyTemplates.map((template) => (
                          <TemplateOption key={`pabbly-${template.id}`} template={template} provider="pabbly" selected={selectedTemplateId === `pabbly:${template.id}`} onSelect={setSelectedTemplateId} />
                        ))}
                      </div>
                    </div>
                  )}
                  {filteredSavedTemplates.length === 0 && filteredPabblyTemplates.length === 0 && (
                    <div className="rounded-2xl bg-slate-50 px-4 py-8 text-center">
                      <p className="text-sm font-semibold text-slate-700">No templates found</p>
                      <p className="mt-1 text-xs text-slate-500">Try a different search.</p>
                    </div>
                  )}
                </div>
              )}

              {pabblyTemplateError && (
                <p className="mt-4 rounded-xl bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-700">{pabblyTemplateError}</p>
              )}
            </div>

            <footer className="mobile-safe-bottom grid grid-cols-2 gap-2 border-t border-slate-200 bg-white p-4 sm:flex sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={closeEmailModal}
                disabled={sendingEmail}
                className="min-h-11 rounded-xl border border-slate-200 px-5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={!selectedTemplateId || templatesLoading || sendingEmail}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sendingEmail ? (
                  <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> Sending…</>
                ) : (
                  <><Icon name="mail" className="h-4 w-4" /> Send email</>
                )}
              </button>
            </footer>
          </section>
        </div>
      )}
    </main>
  );
};

export default CardsPage;
